import React, { useEffect, useRef, useState } from 'react';
import M_PER_NEW_01 from './M_PER_NEW_01';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import useFcmHandler from '../../../../Hooks/useFcmHandler';
import { M_PER_NEW_01_Schema } from './M_PER_NEW_01_Schema';
import {
  useSave1,
  useSearch1,
  useSearch2,
  useSearch4 as useSearchApprovalCandidates,
} from './M_PER_NEW_01_Query';
import { useSearch4 as useSearchRecentApprovalLine } from '../M_PER_NEW/M_PER_NEW_Query';
import { PermitActions } from '../../../../Reducer/PermitReducer';
import { useSave } from '../../../../Hooks/useSave';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import {
  buildDepApprovalFromRows,
  normalizeApprovalLine,
} from '../utils/approvalLine';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import useFileUpload from '../../../../Hooks/useFileUpload';
import Store from '../../../../Store/Store';

export default function M_PER_NEW_01F() {
  const dispatch = useDispatch();
  const { id } = useParams(); // 수정 시 pmCd
  const location = useLocation();
  const searchDailyCd = String(
    location.state?.dailyCd ?? location.state?.DailyCd ?? ''
  );
  const idRef = useRef(id);
  const activeStep = useSelector((state) => state.common.activeStep);
  const { baseInfo, codeInfo, approvalLine, depApproval } = useSelector(
    (state) => state.permit
  );
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const sendFcm = useFcmHandler();
  const activeStepRef = useRef(activeStep);
  const [errors, setErrors] = useState({});
  const [recentApproverRows, setRecentApproverRows] = useState(null);
  const [approvalCandidateRows, setApprovalCandidateRows] = useState([]);

  const { createPreviewItem, SaveLogicFile } = useFileUpload({
    oldFilePathField: 'oldPath',
    newFileNameField: 'saveName',
    getTargetPath: () =>
      `Business/${userInfo.custCd}/SAF_BASIC_02/${userInfo.decUserId}`,
  });

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const { run: SearchApprovalCandidates } = useSearchApprovalCandidates({
    onSuccess: (rows) => {
      const list = rows || [];
      setApprovalCandidateRows(list);
      dispatch(
        PermitActions.setDepApproval(buildDepApprovalFromRows(list, userInfo))
      );
    },
  });

  useEffect(() => {
    if (recentApproverRows === null || depApproval.length === 0) return;

    dispatch(
      PermitActions.initializeApprovalLine(
        normalizeApprovalLine(recentApproverRows, depApproval, {
          userInfo,
          candidateRows: approvalCandidateRows,
        })
      )
    );
  }, [
    recentApproverRows,
    depApproval,
    approvalCandidateRows,
    userInfo,
    dispatch,
  ]);

  // 페이지 진입 시 초기화 및 데이터 로드 (상세 Search2는 Search1 성공 후에만 실행)
  useEffect(() => {
    dispatch(CommonActions.setActiveStep(1));
    dispatch(PermitActions.resetPermitForm('NEW'));

    Search1();
    SearchApprovalCandidates();
  }, [id]);

  // 1. 상세 정보 조회 (수정 모드) — useSearch1 onSuccess보다 먼저 선언
  const { run: Search2 } = useSearch2({
    onSuccess: (response) => {
      const resData = Array.isArray(response) ? response[0] : response;
      if (!resData || !resData.mst) return;

      const mst = resData.mst;
      const firstDaily = resData.dailies?.[0] || {};
      const details = firstDaily.details || [];

      const selectedWorkItemIds = details.map((d) => String(d.workItemId));

      const allCheckedRisks = details
        .flatMap((d) => d.riskItems || [])
        .filter((i) => i.itemCheck === '0');
      const allCheckedEquips = details
        .flatMap((d) => d.equipItems || [])
        .filter((i) => i.itemCheck === '0');
      const allCheckedPreps = details
        .flatMap((d) => d.prepItems || [])
        .filter((i) => i.itemCheck === '0');

      const updateCheckList = (fullList, checkedItems, codeKey) =>
        (fullList || []).map((item) => {
          const isChecked = checkedItems?.some(
            (s) => String(s[codeKey]) === String(item[codeKey])
          );
          return { ...item, itemCheck: isChecked ? 'Y' : '' };
        });

      const latest = Store.getState().permit.codeInfo;

      dispatch(
        PermitActions.setCodeInfo({
          risks: updateCheckList(latest.risks, allCheckedRisks, 'riskCode'),
          equips: updateCheckList(
            latest.equips,
            allCheckedEquips,
            'workEquipCode'
          ),
          preparations: updateCheckList(
            latest.preparations,
            allCheckedPreps,
            'preparationCode'
          ),
        })
      );

      const selectedWorkTypeCodes =
        latest.workTypes
          ?.filter((wt) => selectedWorkItemIds.includes(String(wt.workItemId)))
          .map((wt) => wt.workType) || [];

      const remarkMap = {};
      details.forEach((d) => {
        remarkMap[String(d.workItemId)] = d.etcRemark || '';
      });

      const rawStartDate = mst.totalStartDate?.split(' ')[0] || '';
      const rawEndDate = mst.totalEndDate?.split(' ')[0] || '';
      const startTime = details[0]?.strTime || '09:00';
      const endTime = details[0]?.endTime || '18:00';

      dispatch(
        PermitActions.setBaseInfo({
          ...mst,
          pmCd: String(mst.pmCd),

          workTypes: selectedWorkTypeCodes,
          workItemIds: selectedWorkItemIds,

          workStartDate: `${rawStartDate} ${startTime}`,
          workEndDate: `${rawEndDate} ${endTime}`,

          workPlace: mst.workLoc,
          workDetail: mst.workContent,
          workList: mst.workerList,
          compNm: mst.partnerNm,
          mgrNm: mst.partnerManager,
          mgrTel: mst.partnerTel,

          etcRemarks: remarkMap,
        })
      );
    },
  });

  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      const rawData = rows[0];
      if (!rawData) return;

      const workTypes = rawData.workTypes || [];
      const mapData = (list) =>
        (list || []).map((item) => {
          const parent = workTypes.find(
            (w) => w.workItemId === item.workItemId
          );
          return {
            ...item,
            workType: parent ? parent.workType : '',
            itemCheck: '',
          };
        });

      dispatch(
        PermitActions.setCodeInfo({
          workTypes: workTypes,
          risks: mapData(rawData.riskCodes),
          equips: mapData(rawData.equipCodes),
          preparations: mapData(rawData.preparationCodes),
        })
      );

      const pmCd = idRef.current;
      if (pmCd) {
        if (!searchDailyCd) {
          dispatch(
            showSnackbar({
              message:
                '일일 작업 정보가 없어 수정 데이터를 불러올 수 없습니다. 목록에서 다시 진입해주세요.',
              type: 'warning',
            })
          );
          return;
        }

        Search2({ dailyCd: searchDailyCd });
      }
    },
  });

  // 최근 결재선 자동 세팅
  useSearchRecentApprovalLine({
    onSuccess: (rows) => {
      setRecentApproverRows(rows ?? []);
    },
  });

  const { mutateAsync: save1Async } = useSave1();
  const { run: Save1 } = useSave({
    menuId: 'M_PER_NEW_01',
    schema: M_PER_NEW_01_Schema,
    saveData: (params) => ({ ...params, baseInfo, codeInfo, approvalLine }),

    fileSave: async (requestRows, params) => {
      const { signFile } = params;
      if (signFile) {
        const fileResult = await createPreviewItem(signFile);
        const finalPath = await SaveLogicFile([fileResult]);
        if (finalPath) return { reqSignPath: finalPath };
      }
      return { reqSignPath: '' };
    },

    requestSave: async (rows) => await save1Async(rows[0]),
    showSuccess: false,
    onSuccess: async ({ saveDetailData, userInfo }) => {
      const serverDailyCd =
        saveDetailData?.[0]?.dailyCd ?? saveDetailData?.[0]?.DailyCd ?? '';
      const heads = approvalLine?.depHeads || [];

      for (const head of heads) {
        await sendFcm({
          targetUserId: head.headId,
          requestUserId: userInfo.userid,
          title: '작업허가서 요청',
          body: '새로운 작업 허가서 승인 요청이 있습니다.',
          status: '요청',
          codeGroup: 'AL_002_001',
          route: '/M_Permit',
          responseData: String(serverDailyCd),
        });
      }

      dispatch(PermitActions.setMode('write'));
      dispatch(BottomSheetActions.CloseBottomSheet());
      dispatch(CommonActions.setActiveStep(8));
      dispatch(
        showSnackbar({ message: '요청이 완료되었습니다.', type: 'success' })
      );
    },
  });
  const handleChange = (key, value) => {
    dispatch(PermitActions.setBaseInfo({ [key]: value }));

    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const ValidateAll = () => {
    const fields = M_PER_NEW_01_Schema.fields.baseInfo;
    const newErrors = {};
    let firstErrorMessage = '';

    for (const field in fields) {
      const meta = fields[field];

      if (meta.required !== 'Y' || meta.step !== 1) continue;

      if (!baseInfo[field] || String(baseInfo[field]).trim() === '') {
        newErrors[field] = `${meta.label}은(는) 필수입니다.`;

        if (!firstErrorMessage) {
          firstErrorMessage = newErrors[field];
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      dispatch(
        showSnackbar({
          message: firstErrorMessage,
          type: 'error',
        })
      );
      return false;
    }

    setErrors({});
    return true;
  };

  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom && activeStep < 8,
    onBack: () => {
      if (activeStepRef.current === 1) {
        dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClick' }));
      } else {
        dispatch(CommonActions.decreaseStep());
      }
    },
  });

  return (
    <div className="wrap">
      <M_PER_NEW_01
        id={id}
        Save1={Save1}
        handleChange={handleChange}
        ValidateAll={ValidateAll}
        errors={errors}
      />
    </div>
  );
}
